import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import { API_BASE } from "../config/api";
import ProductCard from "../components/ProductCard/ProductCard";
import {
  DEFAULT_JEWELRY_MENU_GROUPS,
  DEFAULT_JEWELRY_METALS,
  findJewelryMenuGroupById,
  findJewelryMenuGroupByMaterial,
} from "../utils/materialCatalog";
import { formatMaterialDisplayLabel, normalizeMaterialLabel } from "../utils/materialLabels";
import "../pages/ProductGrid.css";
import { useLocation, useNavigate } from "react-router-dom";
import { FaFilter, FaTimes } from "react-icons/fa";
import {
  bindStorefrontRefresh,
  STOREFRONT_REFRESH_SCOPES,
} from "../utils/storefrontRefresh";
import {
  completeStoreListingReturn,
  getStoreListingReturnPosition,
  restoreStoreListingPosition,
} from "../utils/scrollRestoration";
/*

  { label: "Under ₹500", value: "0-500" },
  { label: "₹500 - ₹1000", value: "500-1000" },
  { label: "₹1000 - ₹2000", value: "1000-2000" },
  { label: "₹2000 - ₹5000", value: "2000-5000" },
  { label: "₹5000 - ₹10000", value: "5000-10000" },
  { label: "₹10000 and above", value: "10000+" },
]; */

const PRICE_SORT_OPTIONS = [
  { label: "Price: High to Low", value: "price_high" },
  { label: "Price: Low to High", value: "price_low" },
  { label: "₹0 - ₹999", value: "0-999" },
  { label: "₹1000 - ₹1999", value: "1000-1999" },
  { label: "₹2000 - ₹2999", value: "2000-2999" },
  { label: "₹3000 - ₹3999", value: "3000-3999" },
  { label: "₹4000 and above", value: "4000+" },
];

const GEMSTONE_TYPES = ["PRECIOUS", "SEMI-PRECIOUS", "LAB GROWN (LGD)"];

const GEMSTONE_SHAPES = [
  "Rounds",
  "Emerald cut",
  "Princess cut", 
  "Cushion cut",
  "Heart shape",
  "Marquise",
  "Pear",
  "Trillion",
  "Oval"
];
const GEMSTONE_OPTIONS = {
  "PRECIOUS": [
    "Natural Diamonds",
    "Ruby",
    "Emerald",
    "Coral",
    "Yellow Sapphire",
    "Blue Sapphire",
    "Pearls",
    "Hessonite (Gomed)",
    "Catseye"
  ],
  "SEMI-PRECIOUS": [
    "Amethyst",
    "Peridot",
    "Topaz",
    "Turquoise/Firoza",
    "Malachite",
    "Lapis lazuli",
    "Crystal/Sphatik",
    "Opal",
    "Citrine/Sunela",
    "Pink Tourmaline",
    "Golden pyrite",
    "Rose Quartz",
    "Tiger Eye",
    "Yellow Amber"
  ],
  "LAB GROWN (LGD)": [
    "Diamonds",
    "Ruby",
    "Emerald",
    "Yellow Sapphire",
    "Blue Sapphire"
  ]
};

const ONE_EYE_COCONUT_MATERIAL = "ONE EYE COCONUT (EKAKSHI SHREEFAL)";
const SPIRITUAL_ITEMS = [
  "RUDRAKSH",
  "BLOWING COANCH (SHANKH)",
  "RIGHT HAND COANCH (DAKSHINAVARTI)",
  ONE_EYE_COCONUT_MATERIAL,
];

const normalizeCategoryTokenValue = (value = "") =>
  String(value || "").trim().toUpperCase();

const isHiddenInList = (items, value) =>
  (Array.isArray(items) ? items : []).some((item) => normalizeCategoryTokenValue(item) === normalizeCategoryTokenValue(value));

const CATEGORY_LABELS = {
  PRECIOUS: "Precious",
  "SEMI-PRECIOUS": "Semi-Precious",
  "LAB GROWN (LGD)": "Lab Grown (LGD)",
  RUDRAKSH: "Rudraksh",
  "BLOWING COANCH (SHANKH)": "Blowing Coanch (Shankh)",
  "RIGHT HAND COANCH (DAKSHINAVARTI)": "Right Hand Coanch (Dakshinavarti)",
  [ONE_EYE_COCONUT_MATERIAL]: "One Eye Coconut (Ekakshi Shreefal)",
};

const formatCategoryLabel = (value = "") =>
  CATEGORY_LABELS[value] || formatMaterialDisplayLabel(value) || value;

const normalizeCategoryToken = (value = "") =>
  String(value || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "");

const ONE_EYE_COCONUT_TOKENS = new Set([
  "ONEEYECOCONUTEKAKSHISHREEFAL",
  "ONEEYEDCOCONUTEKAKSHISHREEFAL",
  "ONEEYECOCONUT",
  "ONEEYEDCOCONUT",
  "EKAKSHISHREEFAL",
  "EKAKSHI",
  "SHREEFAL",
]);

const getCategoryContentKeyForMaterial = (material = "") =>
  ONE_EYE_COCONUT_TOKENS.has(normalizeCategoryToken(material))
    ? "one-eyed-coconut"
    : "";

const getFirstContentParagraph = (value = "") => {
  const normalizedValue = String(value || "")
    .replace(/\r\n/g, "\n")
    .trim();

  if (!normalizedValue) {
    return "";
  }

  return (
    normalizedValue
      .split(/\n\s*\n/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean)[0] || normalizedValue
  );
};

const normalizeCategoryList = (categories = []) =>
  [...new Set(
    (Array.isArray(categories) ? categories : [])
      .map((category) => normalizeMaterialLabel(category))
      .filter(Boolean)
  )];

const DEFAULT_STORE_SORT = "price_high";
const PRICE_RANGE_FILTERS = new Set(["0-999", "1000-1999", "2000-2999", "3000-3999", "4000+"]);
const STORE_CATEGORY_PREVIEW_COUNT = 5;
const STORE_CATEGORY_CONFIG = [
  {
    id: "brass-cz-colour-stone-imitation",
    heading: "Metal:Brass",
    subtitle: "",
  },
  {
    id: "stainless-fashion",
    heading: "Metal:Steel",
    subtitle: "",
  },
  {
    id: "silver",
    heading: "Metal:Silver",
    subtitle: "",
  },
  {
    id: "gold",
    heading: "Metal:Gold",
    subtitle: "",
  },
  {
    id: "platinum",
    heading: "Metal:Platinum",
    subtitle: "",
  },
  {
    id: "platinum-gold",
    heading: "Metal:Platinum Gold",
    subtitle: "",
  },
];
const STORE_CATEGORY_GROUPS = STORE_CATEGORY_CONFIG
  .map((group) => ({
    ...group,
    ...findJewelryMenuGroupById(group.id, DEFAULT_JEWELRY_MENU_GROUPS),
  }))
  .filter(Boolean);
const ACTIVE_STORE_CATEGORY_IDS = new Set([
  "brass-cz-colour-stone-imitation",
  "stainless-fashion",
]);
const buildEmptyFeaturedByGroup = () =>
  STORE_CATEGORY_GROUPS.reduce((accumulator, group) => {
    accumulator[group.id] = [];
    return accumulator;
  }, {});

const normalizeFeaturedByGroup = (value = {}) =>
  STORE_CATEGORY_GROUPS.reduce((accumulator, group) => {
    accumulator[group.id] = [...new Set(
      (Array.isArray(value?.[group.id]) ? value[group.id] : [])
        .map((productId) => String(productId || "").trim())
        .filter(Boolean)
    )].slice(0, STORE_CATEGORY_PREVIEW_COUNT);
    return accumulator;
  }, buildEmptyFeaturedByGroup());

const getStoreCategoryMeta = (group) => ({
  heading: group?.heading || group?.label || "",
  subtitle: group?.subtitle || "",
});

const buildFeaturedPreviewProducts = (groupedProducts = [], featuredIds = []) => {
  const productLookup = new Map(
    groupedProducts.map((product) => [String(product?._id || ""), product])
  );
  const featuredProducts = (Array.isArray(featuredIds) ? featuredIds : [])
    .map((productId) => productLookup.get(String(productId || "")))
    .filter(Boolean);
  const seenProductIds = new Set(
    featuredProducts.map((product) => String(product?._id || ""))
  );

  return [
    ...featuredProducts,
    ...groupedProducts.filter(
      (product) => !seenProductIds.has(String(product?._id || ""))
    ),
  ].slice(0, STORE_CATEGORY_PREVIEW_COUNT);
};

const ProductGrid = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSort, setSelectedSort] = useState(DEFAULT_STORE_SORT);
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [selectedGemstone, setSelectedGemstone] = useState("");
  const [selectedGemstoneShape, setSelectedGemstoneShape] = useState("");
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isFeaturedLoading, setIsFeaturedLoading] = useState(true);
  const [isMaterialsLoading, setIsMaterialsLoading] = useState(true);
  const [categoryContent, setCategoryContent] = useState(null);
  const [isCategoryContentExpanded, setIsCategoryContentExpanded] = useState(false);
  const [isCategoryContentLoading, setIsCategoryContentLoading] = useState(false);
  const [materials, setMaterials] = useState(normalizeCategoryList(DEFAULT_JEWELRY_METALS));
  const [gemstoneTypes, setGemstoneTypes] = useState(GEMSTONE_TYPES);
  const [gemstoneOptions, setGemstoneOptions] = useState(GEMSTONE_OPTIONS);
  const [gemstoneShapes, setGemstoneShapes] = useState(GEMSTONE_SHAPES);
  const [spiritualItems, setSpiritualItems] = useState(SPIRITUAL_ITEMS);
  const [featuredByGroup, setFeaturedByGroup] = useState(buildEmptyFeaturedByGroup);
  const location = useLocation();
  const navigate = useNavigate();
  const activeRestoreKeyRef = useRef("");
  const featuredRequestCountRef = useRef(0);
  const materialsRequestCountRef = useRef(0);
  const productsRequestCountRef = useRef(0);
  const latestProductsRequestIdRef = useRef(0);
  const categoryContentRequestCountRef = useRef(0);

  // Parse filters from query string
  const getFiltersFromQuery = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const selectedPrice = searchParams.get("price") || "";

    return {
      material: searchParams.get("material") || "",
      product: searchParams.get("product") || "",
      recipient: searchParams.get("recipient") || "",
      price: selectedPrice,
      sort: searchParams.get("sort") || (selectedPrice ? "" : DEFAULT_STORE_SORT),
      search: searchParams.get("search") || "",
      gemstone: searchParams.get("gemstone") || "",
      gemstoneShape: searchParams.get("gemstoneShape") || "",
      storeCategory: searchParams.get("storeCategory") || "",
    };
  }, [location.search]);

  // Update URL query for price sorting
  const updateQuery = (filterValue) => {
    const searchParams = new URLSearchParams(location.search);

    if (PRICE_RANGE_FILTERS.has(filterValue)) {
      searchParams.set('price', filterValue);
      searchParams.delete('sort');
    } else if (filterValue) {
      searchParams.set('sort', filterValue);
      searchParams.delete('price');
    } else {
      searchParams.delete('sort');
      searchParams.delete('price');
    }

    navigate({ search: searchParams.toString() }, { replace: true });
  };

  // Add a handler for material selection
  const handleMaterialChange = (e) => {
    const value = e.target.value;
    setSelectedMaterial(value);
    const searchParams = new URLSearchParams(location.search);
    if (value) {
      searchParams.set('material', value);
    } else {
      searchParams.delete('material');
    }
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  // Update URL query for specific gemstone
  const handleGemstoneChange = (e) => {
    const value = e.target.value;
    setSelectedGemstone(value);
    setSelectedGemstoneShape(""); // Reset shape when gemstone changes
    const searchParams = new URLSearchParams(location.search);
    if (value) {
      searchParams.set("gemstone", value);
    } else {
      searchParams.delete("gemstone");
    }
    searchParams.delete("gemstoneShape"); // Remove shape filter when gemstone changes
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  // Update URL query for gemstone shape
  const handleGemstoneShapeChange = (e) => {
    const value = e.target.value;
    setSelectedGemstoneShape(value);
    const searchParams = new URLSearchParams(location.search);
    if (value) {
      searchParams.set("gemstoneShape", value);
    } else {
      searchParams.delete("gemstoneShape");
    }
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  const fetchMaterials = useCallback(async () => {
    materialsRequestCountRef.current += 1;
    setIsMaterialsLoading(true);

    try {
      const response = await axios.get(`${API_BASE}/navbar-dropdown`);

      if (Array.isArray(response.data?.jewelryMetals) && response.data.jewelryMetals.length) {
        setMaterials(normalizeCategoryList(response.data.jewelryMetals));
      }
      const hiddenGemstoneTypes = Array.isArray(response.data?.hiddenGemstoneTypes) ? response.data.hiddenGemstoneTypes : [];
      const hiddenGemstoneShapes = Array.isArray(response.data?.hiddenGemstoneShapes) ? response.data.hiddenGemstoneShapes : [];
      const hiddenGemstonesByType = response.data?.hiddenGemstonesByType && typeof response.data.hiddenGemstonesByType === "object"
        ? response.data.hiddenGemstonesByType
        : {};
      const hiddenSpiritualItems = Array.isArray(response.data?.hiddenSpiritualItems) ? response.data.hiddenSpiritualItems : [];

      const nextGemstoneTypes = (Array.isArray(response.data?.gemstoneTypes) && response.data.gemstoneTypes.length
        ? response.data.gemstoneTypes
        : GEMSTONE_TYPES
      ).filter((type) => !isHiddenInList(hiddenGemstoneTypes, type));
      const sourceGemstoneOptions = response.data?.gemstoneOptions && typeof response.data.gemstoneOptions === "object"
        ? response.data.gemstoneOptions
        : GEMSTONE_OPTIONS;
      const nextGemstoneOptions = {};
      nextGemstoneTypes.forEach((type) => {
        nextGemstoneOptions[type] = (Array.isArray(sourceGemstoneOptions[type]) ? sourceGemstoneOptions[type] : [])
          .filter((gemstone) => !isHiddenInList(hiddenGemstonesByType[type], gemstone));
      });

      setGemstoneTypes(nextGemstoneTypes);
      setGemstoneOptions(nextGemstoneOptions);
      setGemstoneShapes((Array.isArray(response.data?.gemstoneShapes) && response.data.gemstoneShapes.length
        ? response.data.gemstoneShapes
        : GEMSTONE_SHAPES
      ).filter((shape) => !isHiddenInList(hiddenGemstoneShapes, shape)));
      setSpiritualItems(SPIRITUAL_ITEMS.filter((item) => !isHiddenInList(hiddenSpiritualItems, item)));
    } catch (error) {
      console.error("Failed to load jewelry metals:", error);
    } finally {
      materialsRequestCountRef.current = Math.max(
        0,
        materialsRequestCountRef.current - 1
      );

      if (materialsRequestCountRef.current === 0) {
        setIsMaterialsLoading(false);
      }
    }
  }, []);

  const fetchStoreFeaturedProducts = useCallback(async () => {
    featuredRequestCountRef.current += 1;
    setIsFeaturedLoading(true);

    try {
      const response = await axios.get(`${API_BASE}/store-featured-products`);
      setFeaturedByGroup(
        normalizeFeaturedByGroup(response.data?.featuredByGroup || {})
      );
    } catch (error) {
      console.error("Failed to load store featured products:", error);
      setFeaturedByGroup(buildEmptyFeaturedByGroup());
    } finally {
      featuredRequestCountRef.current = Math.max(
        0,
        featuredRequestCountRef.current - 1
      );

      if (featuredRequestCountRef.current === 0) {
        setIsFeaturedLoading(false);
      }
    }
  }, []);

  const fetchCategoryContent = useCallback(async () => {
    const { material } = getFiltersFromQuery();
    const categoryKey = getCategoryContentKeyForMaterial(material);

    if (!categoryKey) {
      setCategoryContent(null);
      setIsCategoryContentLoading(false);
      return;
    }

    categoryContentRequestCountRef.current += 1;
    setIsCategoryContentLoading(true);

    try {
      const response = await axios.get(`${API_BASE}/category-content/${categoryKey}`);
      setCategoryContent(response.data || null);
    } catch (error) {
      console.error("Failed to load category content:", error);
      setCategoryContent(null);
    } finally {
      categoryContentRequestCountRef.current = Math.max(
        0,
        categoryContentRequestCountRef.current - 1
      );

      if (categoryContentRequestCountRef.current === 0) {
        setIsCategoryContentLoading(false);
      }
    }
  }, [getFiltersFromQuery]);

  // Fetch products
  const fetchProducts = useCallback(async () => {
    const requestId = latestProductsRequestIdRef.current + 1;
    latestProductsRequestIdRef.current = requestId;
    productsRequestCountRef.current += 1;
    setIsLoading(true);

    try {
      const { material, product, recipient, search, gemstone, gemstoneShape, sort, price, storeCategory } = getFiltersFromQuery();
      const selectedStoreCategory = storeCategory
        ? findJewelryMenuGroupById(storeCategory, STORE_CATEGORY_GROUPS)
        : null;
      const queryParams = { 
        limit: 'all', // Request all products instead of default 10
        _ts: Date.now(),
      };
      if (sort) queryParams.sort = sort;
      if (price) queryParams.price = price;
      if (gemstoneTypes.includes(material)) {
        queryParams.material = material;
        if (gemstone) queryParams.gemstone = gemstone;
        if (gemstoneShape) queryParams.gemstoneShape = gemstoneShape;
      } else if (material) {
        queryParams.material = material;
      } else if (selectedStoreCategory) {
        queryParams.materialGroup = selectedStoreCategory.id;
      }
      if (product) queryParams.product = product;
      if (recipient) queryParams.recipient = recipient;
      if (search) queryParams.search = search;
      const res = await axios.get(`${API_BASE}/products`, {
        params: queryParams,
      });

      if (requestId === latestProductsRequestIdRef.current) {
        setProducts(res.data);
      }
    } catch (err) {
      if (requestId === latestProductsRequestIdRef.current) {
        console.error("Failed to load products:", err);
      }
    } finally {
      productsRequestCountRef.current = Math.max(
        0,
        productsRequestCountRef.current - 1
      );

      if (requestId === latestProductsRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [gemstoneTypes, getFiltersFromQuery]);

  // Fetch when sort/filter changes
  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);

  useEffect(() => {
    fetchStoreFeaturedProducts();
  }, [fetchStoreFeaturedProducts]);

  useEffect(() => {
    fetchCategoryContent();
  }, [fetchCategoryContent]);

  useEffect(() => {
    setIsCategoryContentExpanded(false);
  }, [location.search]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => bindStorefrontRefresh(fetchProducts, { scopes: ["products"] }), [fetchProducts]);
  useEffect(() => bindStorefrontRefresh(fetchMaterials, { scopes: ["navigation"] }), [fetchMaterials]);
  useEffect(() => bindStorefrontRefresh(fetchStoreFeaturedProducts, { scopes: ["products"] }), [fetchStoreFeaturedProducts]);
  useEffect(() => bindStorefrontRefresh(fetchCategoryContent, { scopes: [STOREFRONT_REFRESH_SCOPES.CATEGORY_CONTENT] }), [fetchCategoryContent]);

  useLayoutEffect(() => {
    if (
      isLoading
      || isFeaturedLoading
      || isMaterialsLoading
      || products.length === 0
      || activeRestoreKeyRef.current === location.key
    ) {
      return undefined;
    }

    const returnPosition = getStoreListingReturnPosition(location);

    if (!returnPosition) {
      return undefined;
    }

    let didFinish = false;
    activeRestoreKeyRef.current = location.key;

    const stopRestoring = restoreStoreListingPosition(returnPosition, () => {
      didFinish = true;
      completeStoreListingReturn(location);
    });

    return () => {
      stopRestoring();

      if (!didFinish && activeRestoreKeyRef.current === location.key) {
        activeRestoreKeyRef.current = "";
      }
    };
  }, [isFeaturedLoading, isLoading, isMaterialsLoading, location, products.length]);

  // On mount and when location changes, set state from query
  useEffect(() => {
    const { sort, price, material, gemstone, gemstoneShape } = getFiltersFromQuery();
    setSelectedSort(price || sort || DEFAULT_STORE_SORT);
    setSelectedMaterial(normalizeMaterialLabel(material) || "");
    setSelectedGemstone(gemstone || "");
    setSelectedGemstoneShape(gemstoneShape || "");
  }, [getFiltersFromQuery]);

  // Handle price sort change
  const handlePriceChange = (e) => {
    const value = e.target.value;
    setSelectedSort(value);
    updateQuery(value);
  };

  // Close mobile filter after applying filter
  const handleMobileFilterChange = (handler, value) => {
    handler(value);
    // Close mobile filter after a short delay to show the change
    setTimeout(() => {
      setIsMobileFilterOpen(false);
    }, 500);
  };

  const handleMobilePriceChange = (e) => {
    const value = e.target.value;
    setSelectedSort(value);
    updateQuery(value);
  };

  const clearAllFilters = () => {
    setSelectedMaterial("");
    setSelectedSort(DEFAULT_STORE_SORT);
    setSelectedGemstone("");
    setSelectedGemstoneShape("");
    navigate({ search: "" }, { replace: true });
  };

  const categoryOptions = Array.from(
    new Set([
      ...materials.filter(Boolean),
      ...gemstoneTypes,
      ...spiritualItems,
    ])
  );

  const showGemstoneDropdown = gemstoneTypes.includes(selectedMaterial);
  const currentFilters = getFiltersFromQuery();
  const isPriceSortSelected = currentFilters.sort === "price_high" || currentFilters.sort === "price_low";
  const activeCategoryContentKey = getCategoryContentKeyForMaterial(currentFilters.material);
  const hasActiveCategoryContent = Boolean(activeCategoryContentKey);
  const activeCategoryContent = hasActiveCategoryContent ? categoryContent : null;
  const activeStoreCategory = findJewelryMenuGroupById(
    currentFilters.storeCategory,
    STORE_CATEGORY_GROUPS
  );
  const materialStoreCategory = currentFilters.material
    ? findJewelryMenuGroupByMaterial(
        normalizeMaterialLabel(currentFilters.material),
        STORE_CATEGORY_GROUPS
      )
    : null;
  const inactiveMetalCategory = activeStoreCategory || materialStoreCategory;
  const shouldShowInactiveMetalNote = Boolean(
    inactiveMetalCategory && !ACTIVE_STORE_CATEGORY_IDS.has(inactiveMetalCategory.id)
  );
  const hasCatalogScopedFilters = Boolean(
    currentFilters.material
    || currentFilters.product
    || currentFilters.recipient
    || currentFilters.search
    || currentFilters.gemstone
    || currentFilters.gemstoneShape
  );

  const storeCategoryProducts = STORE_CATEGORY_GROUPS.reduce((groupsMap, group) => {
    groupsMap.set(group.id, []);
    return groupsMap;
  }, new Map());

  products.forEach((product) => {
    const matchedGroup = findJewelryMenuGroupByMaterial(
      normalizeMaterialLabel(product?.material || ""),
      STORE_CATEGORY_GROUPS
    );

    if (!matchedGroup || !storeCategoryProducts.has(matchedGroup.id)) {
      return;
    }

    storeCategoryProducts.get(matchedGroup.id).push(product);
  });

  const storeCategorySections = STORE_CATEGORY_GROUPS
    .map((group) => {
      const groupedProducts = storeCategoryProducts.get(group.id) || [];

      if (!groupedProducts.length) {
        return null;
      }

      return {
        id: group.id,
        heading: getStoreCategoryMeta(group).heading,
        subtitle: getStoreCategoryMeta(group).subtitle,
        products: groupedProducts,
        previewProducts: isPriceSortSelected
          ? groupedProducts.slice(0, STORE_CATEGORY_PREVIEW_COUNT)
          : buildFeaturedPreviewProducts(
              groupedProducts,
              featuredByGroup[group.id]
            ),
        hasMore: groupedProducts.length > STORE_CATEGORY_PREVIEW_COUNT,
      };
    })
    .filter(Boolean);

  const activeStoreCategorySection = activeStoreCategory
    ? {
        id: activeStoreCategory.id,
        heading: getStoreCategoryMeta(activeStoreCategory).heading,
        subtitle: getStoreCategoryMeta(activeStoreCategory).subtitle,
        products: storeCategoryProducts.get(activeStoreCategory.id) || [],
      }
    : null;

  const isSectionedStoreView = !activeStoreCategory && !hasCatalogScopedFilters && storeCategorySections.length > 0;
  const isSingleStoreCategoryView = Boolean(activeStoreCategorySection);
  const visibleProducts = isSingleStoreCategoryView
    ? activeStoreCategorySection.products
    : products;

  const openStoreCategoryView = (categoryId) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("storeCategory", categoryId);
    navigate({ search: searchParams.toString() });
  };

  const closeStoreCategoryView = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("storeCategory");
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  const productPageClassName = [
    "product-page-container",
    isSingleStoreCategoryView ? "product-page-container--store-category-detail" : "",
  ].filter(Boolean).join(" ");

  const renderPriceFilterSelect = () => (
    <div className="store-toolbar-controls">
      <label className="store-toolbar-select">
        <select value={selectedSort} onChange={handlePriceChange}>
          {PRICE_SORT_OPTIONS.map((range) => (
            <option key={range.value} value={range.value}>{range.label}</option>
          ))}
        </select>
      </label>
    </div>
  );

  const renderStoreCategoryDetailHeader = () => {
    if (!isSingleStoreCategoryView) {
      return null;
    }

    return (
      <div className="store-category-detail-header">
        <div className="store-category-heading-copy">
          <h2 className="store-category-title">
            {activeStoreCategorySection.heading}
          </h2>
        </div>
        <div className="store-category-detail-actions">
          {renderPriceFilterSelect()}
          <button
            type="button"
            className="no-products-button store-category-nav-button"
            onClick={closeStoreCategoryView}
          >
            Back
          </button>
        </div>
      </div>
    );
  };

  const renderCategoryContentSection = () => {
    if (!hasActiveCategoryContent) {
      return null;
    }

    if (isCategoryContentLoading && !activeCategoryContent) {
      return (
        <section className="category-content-section">
          <div className="category-content-card">
            <p className="category-content-loading">Loading category content...</p>
          </div>
        </section>
      );
    }

    const pageHeading = String(activeCategoryContent?.pageHeading || "").trim() || "Ekakshi / One-Eyed Coconut";
    const subheading = String(activeCategoryContent?.subheading || "").trim();
    const mainDescription = String(activeCategoryContent?.mainDescription || "").trim();
    const readMoreContent = String(activeCategoryContent?.readMoreContent || "").trim();
    const fullReadMoreContent = readMoreContent || mainDescription;
    const note = String(activeCategoryContent?.note || "").trim();
    const shortDescription = getFirstContentParagraph(mainDescription);
    const descriptionToShow = isCategoryContentExpanded ? fullReadMoreContent : shortDescription;
    const benefits = Array.isArray(activeCategoryContent?.benefits) ? activeCategoryContent.benefits.filter(Boolean) : [];
    const benefitsTitle = String(activeCategoryContent?.benefitsTitle || "").trim() || "Benefits";
    const whyTitle = String(activeCategoryContent?.whyTitle || "").trim() || "Why One Eyed Coconut";
    const whyDescription = String(activeCategoryContent?.whyDescription || "").trim();
    const contentImages = Array.isArray(activeCategoryContent?.images)
      ? activeCategoryContent.images.filter((image) => image?.url)
      : [];
    const readMoreButtonText = String(activeCategoryContent?.buttonText || "").trim() || "Read More";

    return (
      <section className="category-content-section">
        <div className="category-content-card">
          <div className="category-title-block">
            <h1>{pageHeading}</h1>
            {subheading ? <p className="category-content-subheading">{subheading}</p> : null}
          </div>
          {contentImages.length > 0 ? (
            <div className="category-content-gallery">
              {contentImages.map((image, index) => (
                <img
                  src={image.url}
                  alt={image.alt || pageHeading}
                  key={`${image.url}-${index}`}
                  loading="lazy"
                />
              ))}
            </div>
          ) : null}
          {descriptionToShow ? (
            <p className={`category-content-description${isCategoryContentExpanded ? "" : " category-content-preview"}`}>
              {descriptionToShow}
            </p>
          ) : null}
          {isCategoryContentExpanded && benefits.length > 0 ? (
            <div className="category-content-detail-block">
              <h2>{benefitsTitle}</h2>
              <ul>
                {benefits.map((benefit, index) => (
                  <li key={`${benefit}-${index}`}>{benefit}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {isCategoryContentExpanded && whyDescription ? (
            <div className="category-content-detail-block">
              <h2>{whyTitle}</h2>
              <p>{whyDescription}</p>
            </div>
          ) : null}
          {isCategoryContentExpanded && note ? (
            <div className="category-content-note">
              <strong>Note:</strong>
              <p>{note}</p>
            </div>
          ) : null}
          <button
            type="button"
            className="category-read-more-button"
            onClick={() => setIsCategoryContentExpanded((currentValue) => !currentValue)}
          >
            {isCategoryContentExpanded ? "Show Less" : readMoreButtonText}
          </button>
        </div>
      </section>
    );
  };

  const renderCategoryProductsHeading = () => {
    if (!hasActiveCategoryContent) {
      return null;
    }

    return (
      <section className="category-products-section">
        <h2>
          <span>One Eyed Coconut Products</span>
        </h2>
      </section>
    );
  };

  return (
    <div className={productPageClassName}>
      {renderCategoryContentSection()}

      {/* Desktop Filters */}
      {!isSectionedStoreView && !isSingleStoreCategoryView && !hasActiveCategoryContent ? (
        <div className="desktop-filters">
          <div className="store-toolbar-shell">
            {renderPriceFilterSelect()}
          </div>

          {showGemstoneDropdown ? (
            <div className="store-toolbar-secondary">
              <label className="store-toolbar-select">
                <select value={selectedGemstone} onChange={handleGemstoneChange}>
                  <option value="">Choose Your Gemstone</option>
                  {(gemstoneOptions[selectedMaterial] || []).map((gem) => (
                    <option key={gem} value={gem}>{gem}</option>
                  ))}
                </select>
              </label>

              {selectedGemstone ? (
                <label className="store-toolbar-select">
                  <select value={selectedGemstoneShape} onChange={handleGemstoneShapeChange}>
                    <option value="">Choose Shape</option>
                    {gemstoneShapes.map((shape) => (
                      <option key={shape} value={shape}>{shape}</option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Mobile Filter Toggle */}
      {!isSectionedStoreView && !isSingleStoreCategoryView && !hasActiveCategoryContent ? (
      <div className="mobile-filter-toggle">
        <button 
          className="filter-toggle-btn"
          onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
        >
          <FaFilter />
          <span>Filters</span>
        </button>
      </div>
      ) : null}

      {/* Mobile Filter Sidebar */}
      {!isSectionedStoreView && !isSingleStoreCategoryView && !hasActiveCategoryContent ? (
      <div className={`mobile-filter-sidebar ${isMobileFilterOpen ? 'open' : ''}`}>
        <div className="mobile-filter-header">
          <h3>Filters</h3>
          <button 
            className="close-filter-btn"
            onClick={() => setIsMobileFilterOpen(false)}
          >
            <FaTimes />
          </button>
        </div>
        
        <div className="mobile-filter-content">
          <div className="mobile-filter-section">
            <label>Category</label>
            <select
              value={selectedMaterial}
              onChange={handleMaterialChange}
              className="mobile-filter-select"
            >
              <option value="">All Categories</option>
              {categoryOptions.map((option) => (
                <option key={option} value={option}>{formatCategoryLabel(option)}</option>
              ))}
            </select>
          </div>

          {showGemstoneDropdown ? (
            <div className="mobile-filter-section">
              <label>Choose Your Gemstone</label>
              <select
                value={selectedGemstone}
                onChange={handleGemstoneChange}
                className="mobile-filter-select"
              >
                <option value="">Choose Your Gemstone</option>
                {(gemstoneOptions[selectedMaterial] || []).map((gem) => (
                  <option key={gem} value={gem}>{gem}</option>
                ))}
              </select>
            </div>
          ) : null}

          {showGemstoneDropdown && selectedGemstone && (
            <div className="mobile-filter-section">
              <label>Gemstone Shape</label>
              <select 
                value={selectedGemstoneShape} 
                onChange={(e) => handleMobileFilterChange(handleGemstoneShapeChange, e)}
                className="mobile-filter-select"
              >
                <option value="">Choose Shape</option>
                {gemstoneShapes.map((shape) => (
                  <option key={shape} value={shape}>{shape}</option>
                ))}
              </select>
            </div>
          )}

          <div className="mobile-filter-section">
            <label>Sort by Price</label>
            <select
              value={selectedSort}
              onChange={handleMobilePriceChange}
              className="mobile-filter-select"
            >
              {PRICE_SORT_OPTIONS.map((range) => (
                <option key={range.value} value={range.value}>{range.label}</option>
              ))}
            </select>
          </div>

          <div className="mobile-filter-actions">
            <button 
              className="apply-filters-btn"
              onClick={() => setIsMobileFilterOpen(false)}
            >
              Apply Filters
            </button>
            <button 
              className="clear-filters-btn"
              onClick={() => {
                clearAllFilters();
                setIsMobileFilterOpen(false);
              }}
            >
              Clear All
            </button>
          </div>
        </div>
      </div>
      ) : null}

      {/* Mobile Filter Overlay */}
      {!isSectionedStoreView && !isSingleStoreCategoryView && !hasActiveCategoryContent && isMobileFilterOpen && (
        <div 
          className="mobile-filter-overlay"
          onClick={() => setIsMobileFilterOpen(false)}
        />
      )}

      {renderCategoryProductsHeading()}

      {/* Product grid and empty state */}
      {!isLoading && !isSectionedStoreView && visibleProducts.length === 0 ? (
        <div className={isSingleStoreCategoryView ? "store-category-detail" : ""}>
          {renderStoreCategoryDetailHeader()}

          <div className={`no-products-message${hasActiveCategoryContent ? " no-products-message--simple" : ""}`}>
            <h3>COMING SOON...</h3>
            <p>
              We are adding new designs soon. Please try a different category or clear the filters to see all products.
            </p>
            {shouldShowInactiveMetalNote ? (
              <p className="no-products-active-metal-note">
                Currently we are active with Steel and Brass metal jewellery. Please click on Store button to see all the listed products.
              </p>
            ) : null}
            <button
              className="no-products-button"
              onClick={() => navigate("/store", { replace: true })}
            >
              {shouldShowInactiveMetalNote ? "Store" : "Browse All Products"}
            </button>
          </div>
        </div>
      ) : (
        <>
          {isSingleStoreCategoryView ? (
            <div className="store-category-detail">
              {renderStoreCategoryDetailHeader()}

              <div className="product-grid">
                {visibleProducts.map((product) => (
                  <ProductCard product={product} key={product._id} />
                ))}
              </div>
            </div>
          ) : isSectionedStoreView ? (
            <div className="store-category-sections">
              {storeCategorySections.map((section) => {
                return (
                <section className="store-category-section" key={section.id}>
                  <div className="store-category-section-header store-category-section-header--toolbar">
                    <div className="store-category-heading-copy">
                      <h2 className="store-category-title">{section.heading}</h2>
                    </div>
                    {section.hasMore ? (
                      <button
                        type="button"
                        className="no-products-button store-category-nav-button"
                        onClick={() => openStoreCategoryView(section.id)}
                      >
                        {"MORE \u2192"}
                      </button>
                    ) : null}
                  </div>

                  <div className="product-grid">
                    {section.previewProducts.map((product) => (
                      <ProductCard product={product} key={product._id} />
                    ))}
                  </div>

                </section>
                );
              })}
            </div>
          ) : (
            <div className="product-grid">
              {visibleProducts.map((product) => (
                <ProductCard product={product} key={product._id} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductGrid;
